import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import ModalConfirmar from '@/pages/gerenciar/loja/components/ModalConfirmar';
import { iniciais, type Notificar } from './utils';

export interface FuncaoRemovivel {
  chave: string;
  rotulo: string;
  operador_id: number | null;
  portaria_id: number | null;
}

interface Props {
  nome: string;
  email: string;
  funcoes: FuncaoRemovivel[];
  aoFechar: () => void;
  aoSalvo: () => void;
  notificar: Notificar;
}

export default function FolhaFuncoes({
  nome,
  email,
  funcoes,
  aoFechar,
  aoSalvo,
  notificar,
}: Props) {
  const [confirmando, setConfirmando] = useState<FuncaoRemovivel | null>(null);
  const [removendo, setRemovendo] = useState(false);

  async function remover() {
    if (!confirmando) return;
    setRemovendo(true);
    try {
      if (confirmando.operador_id != null) {
        const { error } = await supabase.rpc('loja_remover_operador', {
          p_operador: confirmando.operador_id,
        });
        if (error) throw error;
      } else if (confirmando.portaria_id != null) {
        const { data, error } = await supabase.rpc('equipe_remover_portaria', {
          p_portaria: confirmando.portaria_id,
        });
        if (error) throw error;
        const resp = data as { ok?: boolean; erro?: string } | null;
        if (resp && resp.ok === false) {
          notificar(resp.erro || 'Não foi possível tirar.', 'erro');
          setConfirmando(null);
          return;
        }
      }
      notificar(`${nome} saiu de ${confirmando.rotulo}.`);
      setConfirmando(null);
      aoSalvo();
    } catch (e) {
      notificar(mensagemBanco(e), 'erro');
      setConfirmando(null);
    } finally {
      setRemovendo(false);
    }
  }

  return (
    <>
      <Modal aberto titulo="Funções" aoFechar={aoFechar}>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 text-[15px] font-bold text-primary-700">
            {iniciais(nome, email)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[16px] font-bold text-foreground-950">{nome || 'Pessoa'}</p>
            {email && <p className="truncate text-[13px] text-foreground-500">{email}</p>}
          </div>
        </div>

        {funcoes.length === 0 ? (
          <p className="mt-4 text-[14px] text-foreground-500">Esta pessoa não tem funções ativas.</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {funcoes.map((f) => (
              <li
                key={f.chave}
                className="flex min-h-[52px] items-center gap-3 rounded-2xl bg-background-100 px-3"
              >
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground-950">
                  {f.rotulo}
                </span>
                <button
                  type="button"
                  onClick={() => setConfirmando(f)}
                  className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-danger-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-danger-600 transition-colors hover:bg-danger-50"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-close-line" />
                  </span>
                  Tirar
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5">
          <Botao variante="secundario" cheio onClick={aoFechar}>
            Fechar
          </Botao>
        </div>
      </Modal>

      {confirmando && (
        <ModalConfirmar
          titulo={`Tirar ${nome} de ${confirmando.rotulo}?`}
          mensagem="A pessoa deixa de ter essa função no evento. Dá para chamar de novo depois."
          rotuloConfirmar="Tirar"
          confirmando={removendo}
          aoConfirmar={remover}
          aoCancelar={() => setConfirmando(null)}
        />
      )}
    </>
  );
}